import React from "react";
import { motion } from "framer-motion";
import ReactTooltip from "react-tooltip";
import { AppWrap, MotionWrap } from "../../wrapper";
import { LoadingSpinner } from "../../components";
import { urlFor } from "../../client";
import { useData } from "../../contexts/DataContext";
import { calculateTotalExperience } from "../../utils";
import "./Skills.scss";

const Skills = () => {
  const { skills, loading, error } = useData();
  
  const skillsType = [
    { 
      display: "Programming Languages", 
      prop: "programming",
      icon: "💻",
      description: "Core programming languages I work with"
    },
    { 
      display: "Frameworks & Tools", 
      prop: "frameworks",
      icon: "🛠️",
      description: "Frameworks and development tools"
    }
  ];

  const combinedSkillsType = [
    { 
      display: "Cloud & Databases", 
      prop: "cloud",
      icon: "☁️",
      description: "Cloud platforms & Databases"
    },
    { 
      display: "Core Engineering", 
      prop: "coursework",
      icon: "📚",
      description: "Academic & Core Engineering"
    }
  ];

  if (loading) {
    return <LoadingSpinner message="Loading skills..." />;
  }

  if (error) {
    return <div className="error-message">Error loading data: {error}</div>;
  }

  const getSkillsByCategory = (category) => {
    return skills
      .filter((skill) => skill.type === category)
      .sort((a, b) => {
        if (a.priority > b.priority) return 1;
        if (a.priority < b.priority) return -1;
        return 0;
      });
  };
  
  const totalExperience = calculateTotalExperience(1, 2022);

  return (
    <div className="app__skills-container z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="app__skills-header"
      >
        <h2 className="head-text">Technical Skills</h2>
        <p className="app__skills-subtitle">
          Technologies and tools I use to bring ideas to life
        </p>
      </motion.div>

      {/* Main Skills Categories - Separate Rows */}
      <div className="app__skills-sections">
        {skillsType.map((category, categoryIndex) => (
          <motion.div
            key={category.prop}
            className="app__skills-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: categoryIndex * 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="app__skills-section-header">
              <div className="app__skills-section-icon">{category.icon}</div>
              <div className="app__skills-section-text">
                <h3>{category.display}</h3>
                <p>{category.description}</p>
              </div>
            </div>
            
            <div className="app__skills-grid">
              {getSkillsByCategory(category.prop).map((skill, index) => (
                <motion.div
                  key={skill.name}
                  className="app__skills-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.4, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -8,
                    transition: { duration: 0.2 }
                  }}
                  onMouseEnter={undefined}
                >
                  <div className="app__skills-card-icon">
                    <img src={urlFor(skill.icon)} alt={skill.name} />
                    <h4 className="app__skills-card-name">{skill.name}</h4>
                  </div>
                  
                  {/* Skill level indicator (optional) */}
                  {skill.level && (
                    <div className="app__skills-level">
                      <div className="app__skills-level-bar">
                        <div 
                          className="app__skills-level-fill"
                          style={{ width: `${skill.level}%` }}
                        ></div>
                      </div>
                      <span className="app__skills-level-text">{skill.level}%</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}

        {/* Combined Row for Cloud & Coursework */}
        <motion.div
          className="app__skills-section app__skills-combined"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="app__skills-combined-row">
            {combinedSkillsType.map((category, categoryIndex) => (
              <div key={category.prop} className="app__skills-combined-category">
                <div className="app__skills-section-header">
                  <div className="app__skills-section-icon">{category.icon}</div>
                  <div className="app__skills-section-text">
                    <h3>{category.display}</h3>
                    <p>{category.description}</p>
                  </div>
                </div>
                
                <div className="app__skills-grid">
                  {getSkillsByCategory(category.prop).map((skill, index) => (
                    <motion.div
                      key={skill.name}
                      className="app__skills-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.4, 
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      viewport={{ once: true }}
                      whileHover={{ 
                        y: -8,
                        transition: { duration: 0.2 }
                      }}
                      onMouseEnter={undefined}
                    >
                      <div className="app__skills-card-icon">
                        <img src={urlFor(skill.icon)} alt={skill.name} />
                        <h4 className="app__skills-card-name">{skill.name}</h4>
                      </div>
                      
                      {/* Skill level indicator (optional) */}
                      {skill.level && (
                        <div className="app__skills-level">
                          <div className="app__skills-level-bar">
                            <div 
                              className="app__skills-level-fill"
                              style={{ width: `${skill.level}%` }}
                            ></div>
                          </div>
                          <span className="app__skills-level-text">{skill.level}%</span>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Skills Summary - Updated Stats */}
      <motion.div 
        className="app__skills-summary"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <div className="app__skills-stats">
          <div className="app__skills-stat">
            <span className="app__skills-stat-number">{skills.length}+</span>
            <span className="app__skills-stat-label">Technologies</span>
          </div>
          <div className="app__skills-stat">
            <span className="app__skills-stat-number">4</span>
            <span className="app__skills-stat-label">Specializations</span>
          </div>
          <div className="app__skills-stat">
            <span className="app__skills-stat-number">{totalExperience}+</span>
            <span className="app__skills-stat-label">Years Experience</span>
          </div>
        </div>
      </motion.div>

      <ReactTooltip place="top" type="dark" effect="solid" />
    </div>
  );
};

export default AppWrap(
  MotionWrap(Skills, "app__skills"),
  "skills",
  "app__primarybg"
);
